import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import {
  BarChart3Icon,
  CopyIcon,
  EditIcon,
  EyeIcon,
  Inbox,
  PowerIcon,
  TrashIcon,
} from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { FormsListResult } from "@/modules/form-center/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type FormRow = FormsListResult["data"][number];

type FormsTableProps = {
  data: FormRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (form: FormRow) => void;
  onDetail?: (form: FormRow) => void;
  onDelete?: (id: string) => Promise<void>;
  onChangeStatus?: (
    id: string,
    status: "active" | "under_revision" | "discontinued" | "draft",
  ) => Promise<void>;
  onCopy?: (id: string) => void;
  onViewAnalytics?: (id: string) => void;
  onViewSubmissions?: (id: string) => void;
};

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  under_revision: "ກຳລັງປັບປຸງ",
  discontinued: "ຢຸດໃຊ້ງານ",
  draft: "ຮ່າງ",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  active: "default",
  under_revision: "secondary",
  discontinued: "destructive",
  draft: "outline",
};

// Dynamic color palette for categories
const categoryColorPalette: readonly string[] = [
  "bg-blue-100 text-blue-800",
  "bg-purple-100 text-purple-800",
  "bg-orange-100 text-orange-800",
  "bg-green-100 text-green-800",
  "bg-pink-100 text-pink-800",
  "bg-indigo-100 text-indigo-800",
  "bg-yellow-100 text-yellow-800",
  "bg-cyan-100 text-cyan-800",
  "bg-rose-100 text-rose-800",
  "bg-emerald-100 text-emerald-800",
  "bg-amber-100 text-amber-800",
  "bg-violet-100 text-violet-800",
];

/**
 * Generates a consistent color for a category based on its name
 * Uses a simple hash function to map category names to colors
 */
function getCategoryColor(categoryName: string | null | undefined): string {
  if (!categoryName) {
    return "bg-gray-100 text-gray-800";
  }

  // Simple hash function to convert string to number
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    const char = categoryName.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get index
  const index = Math.abs(hash) % categoryColorPalette.length;
  return categoryColorPalette[index] ?? "bg-gray-100 text-gray-800";
}

export function FormsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDetail,
  onDelete,
  onChangeStatus,
  onCopy,
  onViewAnalytics,
  onViewSubmissions,
}: FormsTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<FormRow>[] = [
    createSortableColumn<FormRow>("name", "ຊື່ຟອມ", {
      size: 250,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded border bg-muted">
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-label="Form document icon"
            >
              <title>Form Document</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    }),
    createSortableColumn<FormRow>("categoryName", "ປະເພດ", {
      size: 120,
      cell: ({ row }) => {
        const categoryName = row.original.categoryName || "-";
        const colorClass = getCategoryColor(categoryName);
        return (
          <Badge className={colorClass} variant="secondary">
            {categoryName}
          </Badge>
        );
      },
    }),
    createSortableColumn<FormRow>("targetAudience", "ກຸ່ມເປົ້າໝາຍ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.targetAudience || "ພະນັກງານທັງໝົດ"}
        </span>
      ),
    }),
    createSortableColumn<FormRow>("status", "ສະຖານະ", {
      size: 120,
      cell: ({ row }) => (
        <Badge variant={statusColors[row.original.status] || "secondary"}>
          <span
            className={`mr-1 h-1.5 w-1.5 rounded-full ${
              row.original.status === "active"
                ? "bg-green-500"
                : row.original.status === "under_revision"
                  ? "bg-yellow-500"
                  : row.original.status === "discontinued"
                    ? "bg-gray-500"
                    : "bg-gray-400"
            }`}
          />
          {statusLabels[row.original.status] || row.original.status}
        </Badge>
      ),
    }),
    createSortableColumn<FormRow>("updatedAt", "ອັບເດດລ່າສຸດ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTime(row.original.updatedAt)}
        </span>
      ),
    }),
    {
      size: 150,
      id: "actions",
      cell: ({ row }: { row: { original: FormRow } }) => {
        const form = row.original;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has(Permissions.forms.read) && onDetail) {
          actions.push({
            label: "ລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onDetail(form),
          });
        }

        if (has(Permissions.forms.read) && onViewSubmissions) {
          actions.push({
            label: "ລາຍການສົ່ງ",
            icon: <Inbox className="h-4 w-4" />,
            onClick: () => onViewSubmissions(form.id),
          });
        }

        if (has(Permissions.forms.update)) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(form),
          });
        }

        if (has(Permissions.forms.update) && onChangeStatus) {
          const currentStatus = form.status;
          const nextStatus =
            currentStatus === "active" ? "discontinued" : "active";
          const statusLabel =
            currentStatus === "active" ? "ປິດການໃຊ້ງານ" : "ເປີດການໃຊ້ງານ";

          actions.push({
            label: statusLabel,
            icon: <PowerIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: statusLabel,
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະ${statusLabel}ຟອມນີ້?`,
                actionText: statusLabel,
                ActionProps: {
                  variant:
                    currentStatus === "active" ? "destructive" : "default",
                },
              });

              if (ok) {
                try {
                  await onChangeStatus(form.id, nextStatus);
                  toast.success(`${statusLabel}ສຳເລັດ`);
                } catch {
                  toast.error(`${statusLabel}ລົ້ມເຫຼວ`);
                }
              }
            },
          });
        }

        if (has(Permissions.forms.delete) && onDelete) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບຟອມ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຟອມນີ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });

              if (ok) {
                try {
                  await onDelete(form.id);
                  toast.success("ລຶບຟອມສຳເລັດ");
                } catch {
                  toast.error("ລຶບຟອມລົ້ມເຫຼວ");
                }
              }
            },
          });
        }

        if (has(Permissions.forms.create) && onCopy) {
          actions.push({
            label: "ສ້າງສຳເນົາລາຍການນີ້",
            icon: <CopyIcon className="h-4 w-4" />,
            onClick: () => onCopy(form.id),
          });
        }

        if (onViewAnalytics) {
          actions.push({
            label: "ການວິເຄາະ",
            icon: <BarChart3Icon className="h-4 w-4" />,
            onClick: () => onViewAnalytics(form.id),
          });
        }

        return <RowActions actions={actions} maxInline={3} />;
      },
    },
  ];

  return (
    <div data-tourid="forms-table">
      <DataTable<FormRow, unknown>
        noDataMessage="ບໍ່ພົບຟອມ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </div>
  );
}
